import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import { formatChronoClock } from "../../utils/coachChronoPlayers";
import { LiveTrainingClubLogo } from "./LiveTrainingClubLogo";
import "./LiveTrainingOffTableLandscape.css";

function liveChronoSeconds(player, now) {
  if (!player?.running || !player.startedAtWall) {
    if (player?.savedSec != null) return Number(player.savedSec) || 0;
    return Number(player?.displaySec) || 0;
  }
  const start = Date.parse(player.startedAtWall);
  if (!Number.isFinite(start)) return Number(player.displaySec) || 0;
  return Math.max(0, ((player.baseMs || 0) + (now - start)) / 1000);
}

function isSameAthlete(row, { userId, playerId }) {
  if (
    userId != null &&
    row?.userId != null &&
    String(row.userId) === String(userId)
  ) {
    return true;
  }
  if (
    playerId != null &&
    row?.playerId != null &&
    String(row.playerId) === String(playerId)
  ) {
    return true;
  }
  return false;
}

/**
 * Landscape station board for off-table exercises: exercise identity,
 * live roster with chrono/count, and the viewer's own result + REC state.
 */
export function LiveTrainingOffTableLandscape({
  chromeHeader = null,
  titleText = "",
  typeText = "",
  subTypeText = "",
  exerciseStateLabel = "",
  isPaused = false,
  isWaiting = false,
  showChrono = true,
  showCount = false,
  countLabel = "",
  socket = null,
  teId = null,
  tsgId = null,
  viewerUserId = null,
  viewerPlayerId = null,
  viewerName = "",
}) {
  const { t } = useTranslation();
  const [chronoPlayers, setChronoPlayers] = useState([]);
  const [countPlayers, setCountPlayers] = useState([]);
  const [countTitle, setCountTitle] = useState(countLabel || "");
  const [videoRecording, setVideoRecording] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    setCountTitle(countLabel || "");
  }, [countLabel]);

  useEffect(() => {
    if (!socket || !teId) {
      setChronoPlayers([]);
      setVideoRecording(false);
      return undefined;
    }
    const onChrono = (payload) => {
      if (!payload || String(payload.te_id) !== String(teId)) return;
      if (
        tsgId &&
        payload.tsg_id &&
        String(payload.tsg_id) !== String(tsgId)
      ) {
        return;
      }
      setChronoPlayers(Array.isArray(payload.players) ? payload.players : []);
      setVideoRecording(Boolean(payload.videoRecording));
    };
    socket.on("offTableChronoSync", onChrono);
    return () => socket.off("offTableChronoSync", onChrono);
  }, [socket, teId, tsgId]);

  useEffect(() => {
    if (!socket || !teId) {
      setCountPlayers([]);
      return undefined;
    }
    const onCount = (payload) => {
      if (!payload || String(payload.te_id) !== String(teId)) return;
      if (
        tsgId &&
        payload.tsg_id &&
        String(payload.tsg_id) !== String(tsgId)
      ) {
        return;
      }
      setCountPlayers(Array.isArray(payload.players) ? payload.players : []);
      if (payload.countLabel) setCountTitle(String(payload.countLabel));
    };
    socket.on("offTableCountSync", onCount);
    return () => socket.off("offTableCountSync", onCount);
  }, [socket, teId, tsgId]);

  const anyChronoRunning = chronoPlayers.some((p) => p.running);
  useEffect(() => {
    if (!anyChronoRunning) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 200);
    return () => window.clearInterval(id);
  }, [anyChronoRunning]);

  const viewerIds = useMemo(
    () => ({ userId: viewerUserId, playerId: viewerPlayerId }),
    [viewerUserId, viewerPlayerId]
  );

  const chronoHasSignal = chronoPlayers.some(
    (p) =>
      p.running ||
      Number(p.displaySec) > 0 ||
      p.savedSec != null ||
      p.recording
  );
  const countHasSignal = countPlayers.some(
    (p) => Number(p.value) > 0 || p.savedValue != null || p.dirty
  );
  const mode =
    showChrono && showCount
      ? chronoHasSignal
        ? "chrono"
        : countHasSignal
          ? "count"
          : "chrono"
      : showCount && !showChrono
        ? "count"
        : showChrono
          ? "chrono"
          : "idle";
  const boardPlayers =
    mode === "count"
      ? countPlayers
      : mode === "chrono"
        ? chronoPlayers
        : [];

  const myChrono = chronoPlayers.find((p) => isSameAthlete(p, viewerIds));
  const myCount = countPlayers.find((p) => isSameAthlete(p, viewerIds));
  const myRecording = Boolean(myChrono?.recording);
  const myChronoSec = myChrono ? liveChronoSeconds(myChrono, now) : 0;
  const myCountValue =
    myCount?.value != null ? Math.max(0, Math.round(Number(myCount.value) || 0)) : null;

  const statusTone = isWaiting ? "wait" : isPaused ? "pause" : "live";
  const drillLabel =
    (countTitle && String(countTitle).trim()) ||
    t("liveTrainingCountModeDefaultLabel", "Reps");

  return (
    <div
      className={`lt-otl${videoRecording ? " lt-otl--cam-live" : ""}${
        myRecording ? " lt-otl--you-rec" : ""
      }`}
    >
      {chromeHeader}
      <div className="lt-otl__board">
        <section className="lt-otl__exercise" aria-label={titleText || "Exercise"}>
          <div className="lt-otl__exercise-brand">
            <LiveTrainingClubLogo className="lt-otl__logo" />
            <span className="lt-otl__eyebrow">
              <DirectionsRunIcon fontSize="inherit" />
              {t("liveTrainingChronoModeEyebrow", "Off-table")}
            </span>
          </div>
          <h2 className="lt-otl__title">{titleText || t("exercise")}</h2>
          <p className="lt-otl__meta">
            {[typeText !== "—" ? typeText : null, subTypeText !== "—" ? subTypeText : null]
              .filter(Boolean)
              .join(" · ") || t("liveTrainingOffTableStation", "Station drill")}
          </p>
          <div className={`lt-otl__status lt-otl__status--${statusTone}`}>
            {exerciseStateLabel ||
              (isWaiting
                ? t("waiting", "Waiting")
                : isPaused
                  ? t("paused", "Paused")
                  : t("live", "Live"))}
          </div>
          <div className="lt-otl__mode-pills">
            {showChrono ? (
              <span className="lt-otl__pill lt-otl__pill--chrono">
                <TimerOutlinedIcon fontSize="inherit" />
                {t("liveTrainingChronoModeTitle", "Chrono")}
              </span>
            ) : null}
            {showCount ? (
              <span className="lt-otl__pill lt-otl__pill--count">
                <FormatListNumberedIcon fontSize="inherit" />
                {drillLabel}
              </span>
            ) : null}
          </div>
        </section>

        <section
          className="lt-otl__lanes"
          aria-label={t("liveTrainingOffTableRoster", "Players")}
        >
          <header className="lt-otl__lanes-head">
            <span>{t("liveTrainingOffTableRoster", "Players")}</span>
            <span>
              {mode === "count"
                ? drillLabel
                : t("liveTrainingChronoModeTitle", "Chrono")}
            </span>
          </header>
          {!boardPlayers.length ? (
            <p className="lt-otl__empty">
              {t(
                "liveTrainingOffTableWaitingCoach",
                "Waiting for coach chrono or count…"
              )}
            </p>
          ) : (
            <ol className="lt-otl__list">
              {boardPlayers.map((player, index) => {
                const mine = isSameAthlete(player, viewerIds);
                const recording = Boolean(player.recording);
                const chronoSec =
                  mode === "chrono" ? liveChronoSeconds(player, now) : 0;
                const countVal =
                  mode === "count"
                    ? Math.max(0, Math.round(Number(player.value) || 0))
                    : null;
                const showMetric =
                  mode === "count" ||
                  player.running ||
                  chronoSec > 0 ||
                  player.savedSec != null;
                return (
                  <li
                    key={player.key || player.userId || player.name || index}
                    className={`lt-otl__lane${mine ? " lt-otl__lane--mine" : ""}${
                      player.running ? " lt-otl__lane--running" : ""
                    }${recording ? " lt-otl__lane--rec" : ""}`}
                  >
                    <span className="lt-otl__lane-order">{index + 1}</span>
                    <span className="lt-otl__lane-name">
                      {player.name}
                      {mine ? (
                        <span className="lt-otl__you">
                          {t("you", "You")}
                        </span>
                      ) : null}
                      {recording ? (
                        <span className="lt-otl__rec" aria-live="polite">
                          REC
                        </span>
                      ) : null}
                    </span>
                    <span className="lt-otl__lane-metric">
                      {showMetric
                        ? mode === "count"
                          ? countVal
                          : formatChronoClock(chronoSec)
                        : "—"}
                    </span>
                  </li>
                );
              })}
            </ol>
          )}
        </section>

        <section
          className={`lt-otl__you-panel${myRecording ? " lt-otl__you-panel--rec" : ""}`}
          aria-label={viewerName || t("you", "You")}
        >
          <p className="lt-otl__you-label">
            {viewerName || t("you", "You")}
            {myRecording ? (
              <span className="lt-otl__rec lt-otl__rec--lg" aria-live="polite">
                REC
              </span>
            ) : null}
          </p>
          {mode === "count" ? (
            <>
              <p className="lt-otl__you-metric" aria-live="polite">
                {myCountValue != null ? myCountValue : "—"}
              </p>
              <p className="lt-otl__you-caption">{drillLabel}</p>
            </>
          ) : (
            <>
              <p className="lt-otl__you-metric lt-otl__you-metric--clock" aria-live="polite">
                {myChrono
                  ? formatChronoClock(myChronoSec)
                  : "00:00.00"}
              </p>
              <p className="lt-otl__you-caption">
                {myChrono?.running
                  ? t("liveTrainingChronoRunning", "Running")
                  : myChrono?.savedSec != null
                    ? t("liveTrainingChronoSaved", "Saved")
                    : t("liveTrainingChronoReady", "Ready")}
              </p>
            </>
          )}
          {videoRecording && !myRecording ? (
            <p className="lt-otl__cam-hint">
              {t("liveTrainingChronoStationRecording", "Station camera recording")}
            </p>
          ) : null}
        </section>
      </div>
    </div>
  );
}

export default LiveTrainingOffTableLandscape;
